/**
 * EmailWithStatus — single source of truth for rendering a customer email
 * with its delivery health.
 *
 * Pairs the address with a small warning triangle when the customer is flagged
 * as bouncing (customers.email_bouncing), so anyone scanning a list or detail
 * page can see at a glance that the address is not landing. Hover reveals when
 * the most recent bounce happened.
 *
 * Use everywhere a customer's email is shown to staff: lists, detail pages,
 * modals, dashboards. Keep raw {customer.email} out of new code.
 */

interface EmailWithStatusProps {
  email: string | null | undefined;
  isBouncing?: boolean | null;
  lastBounceAt?: string | null;
  /** Compact size for table cells. Default false. */
  compact?: boolean;
  /** Fallback to render when email is missing. Default '-'. */
  emptyLabel?: string;
  /** Optional extra class on the wrapper. */
  className?: string;
}

function describeBounceAge(lastBounceAt: string | null | undefined): string {
  if (!lastBounceAt) return 'Last email to this address bounced. Verify the address with the customer before resending.';
  const ts = new Date(lastBounceAt).getTime();
  if (Number.isNaN(ts)) return 'Last email to this address bounced. Verify the address with the customer before resending.';
  const days = Math.floor((Date.now() - ts) / 86_400_000);
  if (days <= 0) return 'Last email to this address bounced today. Verify the address with the customer before resending.';
  if (days === 1) return 'Last email to this address bounced 1 day ago. Verify the address with the customer before resending.';
  return `Last email to this address bounced ${days} days ago. Verify the address with the customer before resending.`;
}

export default function EmailWithStatus({
  email,
  isBouncing,
  lastBounceAt,
  compact = false,
  emptyLabel = '-',
  className,
}: EmailWithStatusProps) {
  if (!email) {
    return <span className={className}>{emptyLabel}</span>;
  }

  if (!isBouncing) {
    return <span className={className}>{email}</span>;
  }

  const tooltip = describeBounceAge(lastBounceAt);
  const iconSize = compact ? '0.75rem' : '0.875rem';

  return (
    <span
      className={className}
      style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}
    >
      <span>{email}</span>
      <span
        role="img"
        aria-label="Email bouncing"
        title={tooltip}
        style={{ fontSize: iconSize, lineHeight: 1, cursor: 'help' }}
      >
        {'⚠️'}
      </span>
    </span>
  );
}
